import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Eye, EyeOff, ShieldCheck, AlertTriangle } from 'lucide-react';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    // Normalize input: Remove leading '@', trim whitespace, and convert to lowercase
    const cleanUsername = username.trim().replace(/^@/, '').toLowerCase();
    const cleanPassword = password.trim();

    if (!cleanUsername || !cleanPassword) {
      setError('Please provide both username and access key.');
      return;
    }

    const success = login(cleanUsername, cleanPassword);
    if (success) {
      navigate(cleanUsername === 'dextertarun' ? '/admin' : '/client');
    } else {
      setError('Invalid platform credentials. Access denied.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-industrial-900">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-brand/10 rounded-2xl mb-6">
            <ShieldCheck className="w-10 h-10 text-brand" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">Platform Access</h1>
          <p className="text-industrial-400 font-light">Enter your secure credentials provided by Dexter.</p>
        </div>

        <div className="glass-card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-industrial-400 mb-2">Username</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-industrial-500">
                  <span className="text-sm font-bold ml-1">@</span>
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-industrial-900 border border-industrial-700 rounded-lg py-3 pl-10 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-brand/50 transition-all"
                  placeholder="Username"
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-industrial-400 mb-2">Access Key</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-industrial-500">
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-industrial-900 border border-industrial-700 rounded-lg py-3 pl-10 pr-12 text-white focus:outline-none focus:ring-2 focus:ring-brand/50 transition-all font-mono"
                  placeholder="••••••••••••"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-industrial-500 hover:text-brand"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {error && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-500 text-sm font-medium animate-shake">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}
            </div>

            <button type="submit" className="btn-primary w-full py-3 flex items-center justify-center gap-2 shadow-lg shadow-brand/10">
              <Rocket className="w-5 h-5" />
              Unlock Platform
            </button>
          </form>

          <div className="mt-6 text-center">
            <button 
              onClick={() => alert('Please contact administrator (tarun@dexterdesigns.in) for credentials.')}
              className="text-xs text-industrial-500 hover:text-brand transition-colors underline underline-offset-4"
            >
              Forgot Access Key?
            </button>
          </div>
        </div>

        <p className="mt-8 text-center text-industrial-500 text-sm">
          Protected by Dexter Communications Security
        </p>
      </div>
    </div>
  );
}

// Re-using Rocket icon from Home
const Rocket = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4.5c1.62-1.63 5-2 5-2"/><path d="M12 15v5s3.03-.55 4.5-2c1.63-1.62 2-5 2-5"/></svg>
);
